import React from 'react';
import { Header, Footer } from '../components/layout';
import { Bot, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const BotPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col justify-between bg-slate-50 dark:bg-slate-950 transition-colors">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 space-y-8">
          <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                PriceLossBot Information & Crawler Disclosure
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Transparent, polite, and respectful price verification architecture
              </p>
            </div>
          </div>

          <section className="space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Crawler Identity
            </h2>
            <p>
              PriceLoss operates an automated price verification bot that fetches publicly viewable ecommerce product pages solely to verify purchasable prices on behalf of registered consumers.
            </p>
            <div className="p-4 bg-slate-100 dark:bg-slate-800/80 rounded-xl font-mono text-xs text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
              User-Agent: PriceLossBot/1.0 (+https://priceloss.com/bot)
            </div>
          </section>

          <section className="space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Politeness & Politeness Guarantees
            </h2>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Strict Rate Limiting:</strong> Enforced minimum interval between requests and strict concurrency caps per domain managed via distributed Redis leases.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Public Pages Only:</strong> We never crawl behind login walls, paywalls, member-only portals, or cart/checkout endpoints.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <span>
                  <strong>SafeURLPolicy Enforcement:</strong> All fetches validate IP resolution against loopback, private RFC1918, and link-local ranges before network connection.
                </span>
              </li>
            </ul>
          </section>

          <section className="space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed border-t border-slate-200 dark:border-slate-800 pt-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Opt-Out & Delisting
            </h2>
            <p>
              If you are a store administrator and prefer that PriceLossBot does not check prices on your domain, you can block our user-agent in your <code>robots.txt</code> or request permanent delisting on our{' '}
              <Link to="/abuse" className="text-emerald-600 dark:text-emerald-400 underline font-medium">
                Abuse & Delisting page
              </Link>
              .
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
